import React, { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Save,
  Globe,
  ArrowLeft,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Upload,
  Loader2,
  Image as ImageIcon,
  MessageSquare,
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { INDUSTRIES, DEFAULT_HOURS } from '../../utils/industrySchemas'

// ─── Field renderers (identical to ContentEditor) ────────────────────────────

const TextField = ({ field, value, onChange }) => (
  <div>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
      {field.label}
    </label>
    <input
      type={field.type === 'url' ? 'url' : 'text'}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder}
      className="ds-input w-full"
    />
  </div>
)

const TextAreaField = ({ field, value, onChange }) => (
  <div>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
      {field.label}
    </label>
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder}
      rows={4}
      className="ds-input w-full"
      style={{ resize: 'vertical' }}
    />
  </div>
)

const ImageField = ({ field, value, onChange, clientId }) => {
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('files', file)
      if (clientId) form.append('clientId', clientId)
      const res = await apiService.post('/media/upload', form)
      onChange(res.data.files?.[0]?.public_url)
      toast.success('Image uploaded')
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
        {field.label}
      </label>
      <div className="flex items-center gap-3">
        {value ? (
          <div style={{ position: 'relative', width: 80, height: 80, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '0'}
          >
            <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button
              onClick={() => onChange('')}
              className="img-overlay"
              style={{
                position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)',
                opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', cursor: 'pointer', transition: 'opacity .15s',
              }}
            >
              <Trash2 size={16} style={{ color: 'white' }} />
            </button>
          </div>
        ) : (
          <div style={{
            width: 80, height: 80, borderRadius: 10, border: '2px dashed var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <ImageIcon size={24} style={{ color: 'var(--text-faint)' }} />
          </div>
        )}
        <label
          className="btn btn-ghost btn-sm"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', opacity: uploading ? 0.5 : 1, pointerEvents: uploading ? 'none' : 'auto' }}
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Uploading...' : 'Upload image'}
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  )
}

const MultiImageField = ({ field, value = [], onChange, clientId }) => {
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setUploading(true)
    try {
      const urls = await Promise.all(files.map(async (file) => {
        const form = new FormData()
        form.append('files', file)
        if (clientId) form.append('clientId', clientId)
        const res = await apiService.post('/media/upload', form)
        return res.data.files?.[0]?.public_url
      }))
      onChange([...value, ...urls])
      toast.success(`${urls.length} image(s) uploaded`)
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const removeImage = (index) => onChange(value.filter((_, i) => i !== index))

  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
        {field.label}
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
        {value.map((url, i) => (
          <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}
            onMouseEnter={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '0'}
          >
            <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button
              onClick={() => removeImage(i)}
              className="img-overlay"
              style={{
                position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)',
                opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', cursor: 'pointer', transition: 'opacity .15s',
              }}
            >
              <Trash2 size={14} style={{ color: 'white' }} />
            </button>
          </div>
        ))}
        <label style={{
          aspectRatio: '1', borderRadius: 10, border: '2px dashed var(--border)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'border-color .15s',
          opacity: uploading ? 0.5 : 1, pointerEvents: uploading ? 'none' : 'auto',
        }}>
          {uploading
            ? <Loader2 size={18} className="animate-spin" style={{ color: 'var(--text-faint)' }} />
            : <Plus size={18} style={{ color: 'var(--text-faint)' }} />}
          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  )
}

const HoursField = ({ value = DEFAULT_HOURS, onChange }) => (
  <div>
    <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid var(--border)' }}>
      <table style={{ width: '100%', minWidth: 360, fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 500, color: 'var(--text-muted)' }}>Day</th>
            <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 500, color: 'var(--text-muted)' }}>Status</th>
            <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 500, color: 'var(--text-muted)' }}>Opens</th>
            <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 500, color: 'var(--text-muted)' }}>Closes</th>
          </tr>
        </thead>
        <tbody>
          {value.map((row, i) => (
            <tr key={row.day} style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '10px 16px', fontWeight: 500, color: 'var(--text)' }}>{row.day}</td>
              <td style={{ padding: '10px 16px' }}>
                <button
                  type="button"
                  onClick={() => {
                    const updated = [...value]
                    updated[i] = { ...row, open: !row.open }
                    onChange(updated)
                  }}
                  className="badge"
                  style={{
                    background: row.open ? 'rgba(34,197,94,.12)' : 'var(--surface-2)',
                    color: row.open ? 'var(--success)' : 'var(--text-muted)',
                    cursor: 'pointer',
                    border: 'none',
                  }}
                >
                  {row.open ? 'Open' : 'Closed'}
                </button>
              </td>
              <td style={{ padding: '10px 16px' }}>
                <input
                  type="time"
                  value={row.openTime}
                  disabled={!row.open}
                  onChange={e => {
                    const updated = [...value]
                    updated[i] = { ...row, openTime: e.target.value }
                    onChange(updated)
                  }}
                  className="ds-input"
                  style={{ opacity: row.open ? 1 : 0.4, cursor: row.open ? 'auto' : 'not-allowed' }}
                />
              </td>
              <td style={{ padding: '10px 16px' }}>
                <input
                  type="time"
                  value={row.closeTime}
                  disabled={!row.open}
                  onChange={e => {
                    const updated = [...value]
                    updated[i] = { ...row, closeTime: e.target.value }
                    onChange(updated)
                  }}
                  className="ds-input"
                  style={{ opacity: row.open ? 1 : 0.4, cursor: row.open ? 'auto' : 'not-allowed' }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)

const ListField = ({ fieldDef, value = [], onChange, clientId }) => {
  const [expanded, setExpanded] = useState(null)

  const addItem = () => {
    const newItem = {}
    fieldDef.subFields.forEach(sf => { newItem[sf.key] = sf.type === 'multi-image' ? [] : '' })
    const updated = [...value, newItem]
    onChange(updated)
    setExpanded(updated.length - 1)
  }

  const removeItem = (index) => {
    onChange(value.filter((_, i) => i !== index))
    if (expanded === index) setExpanded(null)
  }

  const updateItem = (index, key, val) => {
    const updated = [...value]
    updated[index] = { ...updated[index], [key]: val }
    onChange(updated)
  }

  return (
    <div className="col gap-2">
      {value.map((item, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'var(--surface-2)' }}>
            <button
              type="button"
              onClick={() => setExpanded(expanded === i ? null : i)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {expanded === i ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {item[fieldDef.subFields[0]?.key] || `Item ${i + 1}`}
            </button>
            <button
              type="button"
              onClick={() => removeItem(i)}
              className="btn btn-ghost btn-icon btn-sm"
              style={{ color: 'var(--text-faint)' }}
            >
              <Trash2 size={14} />
            </button>
          </div>

          {expanded === i && (
            <div className="col gap-4 card-pad" style={{ background: 'var(--surface)' }}>
              {fieldDef.subFields.map(sf => (
                <div key={sf.key}>
                  {sf.type === 'text' || sf.type === 'url' ? (
                    <TextField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} />
                  ) : sf.type === 'textarea' ? (
                    <TextAreaField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} />
                  ) : sf.type === 'image' ? (
                    <ImageField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} clientId={clientId} />
                  ) : sf.type === 'multi-image' ? (
                    <MultiImageField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} clientId={clientId} />
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <button type="button" onClick={addItem} className="btn btn-ghost w-full" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        <Plus size={14} />
        {fieldDef.addLabel || 'Add Item'}
      </button>
    </div>
  )
}

const FieldRenderer = ({ field, value, onChange, clientId }) => {
  switch (field.type) {
    case 'text':
    case 'url':
      return <TextField field={field} value={value} onChange={onChange} />
    case 'textarea':
      return <TextAreaField field={field} value={value} onChange={onChange} />
    case 'image':
      return <ImageField field={field} value={value} onChange={onChange} clientId={clientId} />
    case 'multi-image':
      return <MultiImageField field={field} value={value} onChange={onChange} clientId={clientId} />
    case 'hours':
      return <HoursField value={value} onChange={onChange} />
    case 'list':
      return <ListField fieldDef={field} value={value} onChange={onChange} clientId={clientId} />
    default:
      return null
  }
}

// ─── ContentEditorForm ────────────────────────────────────────────────────────
// Props:
//   siteId          — required, the site to edit
//   clientId        — optional; if provided, fetches /clients/profile?clientId=X (admin context)
//   onBack          — optional; if provided shows a back/close button that calls this
//   showRequestChange — bool (default true); whether to show "Request a Change" button

const ContentEditorForm = ({ siteId, clientId, onBack, showRequestChange = true }) => {
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState(null)
  const [content,   setContent]   = useState({})
  const [hasUnsaved, setHasUnsaved] = useState(false)

  const { data: site, isLoading: siteLoading } = useQuery(
    ['site', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { enabled: !!siteId }
  )

  // When clientId is provided (admin editing on behalf of a client), fetch that client's profile
  const profileUrl = clientId ? `/clients/profile?clientId=${clientId}` : '/clients/profile'
  const { data: clientProfile, isLoading: profileLoading } = useQuery(
    ['client-profile', clientId || 'self'],
    () => apiService.get(profileUrl).then(r => r.data),
  )

  useEffect(() => {
    if (site?.meta_data) {
      try {
        const parsed = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : site.meta_data
        setContent(parsed || {})
      } catch {
        setContent({})
      }
    }
  }, [site])

  const industry = clientProfile?.industry || 'general'
  const schema   = INDUSTRIES[industry] || INDUSTRIES.general

  useEffect(() => {
    if (schema && !activeTab) setActiveTab(schema.tabs[0]?.id)
  }, [schema])

  const updateField = useCallback((key, value) => {
    setContent(prev => ({ ...prev, [key]: value }))
    setHasUnsaved(true)
  }, [])

  const saveMutation = useMutation(
    (meta) => apiService.put(`/sites/${siteId}`, { meta_data: JSON.stringify(meta) }),
    {
      onSuccess: () => {
        toast.success('Content saved!')
        setHasUnsaved(false)
        queryClient.invalidateQueries(['site', siteId])
      },
      onError: () => toast.error('Failed to save content'),
    }
  )

  const publishMutation = useMutation(
    async () => {
      await saveMutation.mutateAsync(content)
      return apiService.post(`/sites/${siteId}/publish`)
    },
    {
      onSuccess: () => toast.success('Site published!'),
      onError:   () => toast.error('Failed to publish'),
    }
  )

  if (siteLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 256 }}>
        <LoadingSpinner size="lg" text="Loading editor..." />
      </div>
    )
  }

  const activeTabSchema = schema.tabs.find(t => t.id === activeTab)
  // The clientId to use for image uploads — prefer the explicit prop, fall back to the profile's id
  const uploadClientId = clientId || clientProfile?.id

  return (
    <div className="col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
          {onBack && (
            <button
              onClick={onBack}
              className="btn btn-ghost btn-icon btn-sm"
              style={{ flexShrink: 0 }}
            >
              <ArrowLeft size={14} />
            </button>
          )}
          <div style={{ minWidth: 0 }}>
            <h1 className="font-bold truncate" style={{ fontSize: 20, color: 'var(--text)' }}>
              {site?.name || 'Edit Content'}
            </h1>
            <p className="text-muted" style={{ fontSize: 13, marginTop: 2 }}>
              {schema.icon} {schema.label} — update website content below
            </p>
          </div>
          {hasUnsaved && (
            <span style={{ flexShrink: 0, fontSize: 12, color: 'var(--warning)', fontWeight: 500 }}>• Unsaved</span>
          )}
        </div>

        <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
          {showRequestChange && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => window.location.href = '/client/requests/new'}
            >
              <MessageSquare size={13} />
              <span>Request a Change</span>
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => saveMutation.mutate(content)}
            disabled={saveMutation.isLoading}
          >
            {saveMutation.isLoading ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Save
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => publishMutation.mutate()}
            disabled={publishMutation.isLoading}
          >
            {publishMutation.isLoading ? <Loader2 size={13} className="animate-spin" /> : <Globe size={13} />}
            Publish
          </button>
        </div>
      </div>

      {/* Mobile tab bar — only shown ≤768px (the left side-nav replaces it on desktop) */}
      <div className="flex gap-1 editor-tabs-mobile" style={{ overflowX: 'auto', paddingBottom: 4 }}>
        {schema.tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex gap-4">
        {/* Sidebar tabs — desktop only; hidden ≤768px so the mobile tab bar above
            is the single source of navigation (avoids a duplicated menu and frees
            the full width for the form). */}
        <div className="editor-tabs-side" style={{ width: 176, flexShrink: 0 }}>
          <nav className="col gap-1">
            {schema.tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'btn btn-primary btn-sm w-full' : 'btn btn-ghost btn-sm w-full'}
                style={{ textAlign: 'left', justifyContent: 'flex-start' }}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="grow" style={{ minWidth: 0 }}>
          {activeTabSchema && (
            <div className="card">
              <div className="card-pad" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
                <h2 className="font-semibold" style={{ fontSize: 15, color: 'var(--text)' }}>{activeTabSchema.label}</h2>
              </div>
              <div className="card-pad col gap-4">
                {activeTabSchema.fields.map(field => (
                  <FieldRenderer
                    key={field.key}
                    field={field}
                    value={content[field.key]}
                    onChange={val => updateField(field.key, val)}
                    clientId={uploadClientId}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ContentEditorForm
